import React from 'react';

function buildSparkCoords(vals, w, h) {
  const max = Math.max(...vals, 1);
  const min = Math.min(...vals, 0);
  const range = max - min || 1;
  return vals.map((v, i) => {
    const x = vals.length <= 1 ? w / 2 : (i / (vals.length - 1)) * w;
    const y = h - ((v - min) / range) * (h - 6) - 3;
    return `${x},${y}`;
  }).join(' ');
}

function Sparkline({ points, field, modoOscuro, color = 'blue', className = 'h-8' }) {
  const vals = points.map((p) => Number(p[field]) || 0);
  const w = 100;
  const h = 32;
  const coords = buildSparkCoords(vals, w, h);
  const palette = {
    blue: {
      stroke: modoOscuro ? '#5CA3FF' : '#1F80FF',
      fill: modoOscuro ? 'rgba(31,128,255,0.12)' : 'rgba(31,128,255,0.1)',
      dot: modoOscuro ? '#5CA3FF' : '#1F80FF',
    },
    amber: {
      stroke: modoOscuro ? '#FBBF24' : '#D97706',
      fill: modoOscuro ? 'rgba(251,191,36,0.12)' : 'rgba(217,119,6,0.1)',
      dot: modoOscuro ? '#FBBF24' : '#D97706',
    },
  };
  const { stroke, fill, dot } = palette[color] || palette.blue;
  const lastX = vals.length <= 1 ? w / 2 : w;
  const lastY = coords.split(' ').pop()?.split(',')[1] || h / 2;

  if (points.length < 2) return null;

  return (
    <svg viewBox={`0 0 ${w} ${h}`} className={`w-full ${className}`} preserveAspectRatio="none" aria-hidden>
      <polygon points={`0,${h} ${coords} ${w},${h}`} fill={fill} />
      <polyline
        points={coords}
        fill="none"
        stroke={stroke}
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx={lastX} cy={lastY} r="2.5" fill={dot} />
    </svg>
  );
}

function MonthRow({ points, field, suffix, textPri, textMuted }) {
  return (
    <div className="flex justify-between gap-0.5 mt-2">
      {points.map((p, idx) => {
        const val = Number(p[field]) || 0;
        const isLast = idx === points.length - 1;
        return (
          <div key={`${field}-${p.mes}`} className="flex-1 min-w-0 text-center">
            <div className={`text-[9px] truncate ${textMuted}`}>{p.label}</div>
            <div className={`text-[10px] font-semibold tabular-nums ${
              val > 0 || isLast ? textPri : textMuted
            } ${isLast ? 'opacity-100' : val > 0 ? 'opacity-90' : 'opacity-40'}`}>
              {val}{suffix}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function TrendBlock({ title, hint, value, valueTone, children, textMuted }) {
  return (
    <div className="min-w-0">
      <div className="flex items-baseline justify-between gap-2 mb-1.5">
        <div className="min-w-0">
          <div className={`text-[11px] font-semibold ${textMuted}`}>{title}</div>
          {hint && <div className={`text-[10px] ${textMuted} opacity-75`}>{hint}</div>}
        </div>
        {value != null && (
          <span className={`text-sm font-bold tabular-nums shrink-0 ${valueTone}`}>{value}</span>
        )}
      </div>
      {children}
    </div>
  );
}

export default function CollectionTrendChart({
  points = [],
  modoOscuro,
  textPri,
  textMuted,
  compact = false,
  showDso = true,
}) {
  const last = points[points.length - 1];

  if (!points.length) return null;

  const tasaTone = (last?.tasaCobranza ?? 0) >= 80
    ? modoOscuro ? 'text-green-300' : 'text-green-700'
    : modoOscuro ? 'text-[#5CA3FF]' : 'text-[#004AAD]';

  const dsoTone = (last?.dso ?? 0) > 45
    ? modoOscuro ? 'text-amber-300' : 'text-amber-700'
    : modoOscuro ? 'text-sky-300' : 'text-sky-700';

  if (compact) {
    return (
      <div className={`rounded-xl px-3 py-2.5 ${modoOscuro ? 'bg-gray-800/50' : 'bg-gray-50'}`}>
        <div className="flex items-center justify-between gap-2 mb-1.5">
          <span className={`text-[10px] font-semibold uppercase tracking-wide ${textMuted}`}>
            Tasa · 6 meses
          </span>
          <span className={`text-xs font-bold tabular-nums ${tasaTone}`}>{last?.tasaCobranza}%</span>
        </div>
        <Sparkline points={points} field="tasaCobranza" modoOscuro={modoOscuro} className="h-8" />
        <div className="flex justify-between mt-1">
          <span className={`text-[9px] ${textMuted}`}>{points[0]?.label}</span>
          <span className={`text-[9px] ${textMuted}`}>{last?.label}</span>
        </div>
      </div>
    );
  }

  const panel = modoOscuro ? 'bg-gray-800/40 border-gray-700/80' : 'bg-gray-50/90 border-gray-200/80';

  return (
    <div className={`rounded-xl border p-3 ${panel}`}>
      <div className={`grid gap-4 ${showDso ? 'sm:grid-cols-2' : 'grid-cols-1'}`}>
        <TrendBlock
          title="Tasa de cobranza"
          hint="% cobrado / facturado"
          value={`${last?.tasaCobranza ?? 0}%`}
          valueTone={tasaTone}
          textMuted={textMuted}
        >
          <Sparkline points={points} field="tasaCobranza" modoOscuro={modoOscuro} className="h-9" />
          <MonthRow
            points={points}
            field="tasaCobranza"
            suffix="%"
            textPri={textPri}
            textMuted={textMuted}
          />
        </TrendBlock>

        {showDso && (
          <TrendBlock
            title="DSO del período"
            hint="Días pendientes del mes"
            value={`${last?.dso ?? 0}d`}
            valueTone={dsoTone}
            textMuted={textMuted}
          >
            <Sparkline points={points} field="dso" modoOscuro={modoOscuro} color="amber" className="h-9" />
            <MonthRow
              points={points}
              field="dso"
              suffix="d"
              textPri={textPri}
              textMuted={textMuted}
            />
          </TrendBlock>
        )}
      </div>
    </div>
  );
}
